import React, { useEffect, useState } from "react";
import { Plus, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";

const CATEGORIES = ["Rent", "Salaries", "Utilities", "Supplies", "Marketing", "Travel", "Software", "Bank Charges", "Other"];

function ExpenseModal({ open, onClose, onSaved, baseCurrency }) {
  const [f, setF] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: "Other", vendor_name: "", amount: 0,
    currency: baseCurrency, exchange_rate: 1, tax_amount: 0, tax_eligible: false,
    payment_mode: "Cash", notes: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF((s) => ({ ...s, currency: baseCurrency })); /* eslint-disable-next-line */ }, [open, baseCurrency]);
  if (!open) return null;
  const save = async () => {
    if (!f.category || f.amount <= 0) { toast.error("Category and amount required"); return; }
    setSaving(true);
    try { await api.post("/expenses", f); toast.success("Expense added"); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="expense-modal">
      <div className="bg-white rounded-lg w-full max-w-xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-4">New Expense</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="label-field">Date *</label>
            <input data-testid="exp-date" type="date" className="input-clean" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })}/></div>
          <div><label className="label-field">Category *</label>
            <select data-testid="exp-category" className="input-clean" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select></div>
          <div><label className="label-field">Vendor / Description</label>
            <input className="input-clean" value={f.vendor_name} onChange={(e) => setF({ ...f, vendor_name: e.target.value })}/></div>
          <div><label className="label-field">Amount *</label>
            <input data-testid="exp-amount" type="number" step="0.01" className="input-clean font-mono-data" value={f.amount} onChange={(e) => setF({ ...f, amount: parseFloat(e.target.value) || 0 })}/></div>
          <div><label className="label-field">Currency</label>
            <input className="input-clean font-mono-data uppercase" maxLength={3} value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })}/></div>
          <div><label className="label-field">Exchange Rate</label>
            <input type="number" step="0.0001" className="input-clean font-mono-data" value={f.exchange_rate} onChange={(e) => setF({ ...f, exchange_rate: parseFloat(e.target.value) || 1 })}/></div>
          <div><label className="label-field">Tax Amount</label>
            <input type="number" step="0.01" className="input-clean font-mono-data" value={f.tax_amount} onChange={(e) => setF({ ...f, tax_amount: parseFloat(e.target.value) || 0 })}/></div>
          <div><label className="label-field">Payment Mode</label>
            <select className="input-clean" value={f.payment_mode} onChange={(e) => setF({ ...f, payment_mode: e.target.value })}>
              <option>Cash</option><option>Bank</option><option>Card</option><option>UPI</option><option>Cheque</option>
            </select></div>
          <div className="md:col-span-2 flex items-center gap-2">
            <input type="checkbox" checked={f.tax_eligible} onChange={(e) => setF({ ...f, tax_eligible: e.target.checked })} data-testid="exp-tax-eligible"/>
            <label className="text-sm text-[#64748B]">Tax eligible (claim input credit)</label>
          </div>
          <div className="md:col-span-2"><label className="label-field">Notes</label>
            <textarea className="input-clean" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}/></div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button data-testid="exp-save-btn" onClick={save} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Expenses() {
  const { active } = useCompany();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!active) return;
    try {
      const { data } = await api.get("/expenses");
      setRows(data.expenses || []);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); }, [active]);

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Expenses</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">Business Expenses</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-expense-btn" className="btn-primary w-full sm:w-auto justify-center">
          <Plus size={14}/> New Expense
        </button>
      </div>

      <div className="card-surface p-4">
        {rows.length === 0 ? (
          <div className="text-center py-12">
            <Receipt size={48} className="mx-auto text-[#CBD5E1] mb-3"/>
            <p className="text-[#64748B]">No expenses yet.</p>
          </div>
        ) : (
          <table className="table-clean">
            <thead>
              <tr>
                <th>Date</th><th>Category</th><th>Vendor</th><th>Mode</th>
                <th className="text-right">Amount</th><th className="text-right">Base</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} data-testid={`expense-row-${e.id}`}>
                  <td className="font-mono-data">{e.date}</td>
                  <td>{e.category}</td>
                  <td>{e.vendor_name || "-"}</td>
                  <td className="text-sm">{e.payment_mode}</td>
                  <td className="text-right font-mono-data">{fmtMoney(e.amount, e.currency)}</td>
                  <td className="text-right font-mono-data">{fmtMoney(e.base_amount, active?.base_currency)}</td>
                  <td><button onClick={() => remove(e.id)} className="text-[#DC2626]"><Trash2 size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <ExpenseModal open={open} onClose={() => setOpen(false)} onSaved={load} baseCurrency={active?.base_currency || "INR"} />
    </div>
  );
}
